import { z } from 'zod'

const id = z.string()
const plan = z.object({ location_id: id, day: z.string(), dish_id: id, quantity: z.number().int().min(0), revision: z.number().int().positive(), updated_at: z.string() })
const stock = z.object({ location_id: id, item_key: z.string(), status: z.enum(['unknown', 'enough', 'low', 'out']), note: z.string(), revision: z.number().int().positive(), updated_at: z.string() })
export const boardSchema = z.object({
 chef_profile_id: id, start_day: z.string(), read_at: z.string(),
 locations: z.array(z.object({ id, label: z.string(), address: z.string() })).max(100),
 dishes: z.array(z.object({ id, name: z.string(), ingredients: z.array(z.string()) })).max(200),
 stock_items: z.array(z.object({ key: z.string(), name: z.string(), dish_ids: z.array(id) })).max(1000),
 plans: z.array(plan).max(10000), stock: z.array(stock).max(10000),
 orders: z.array(z.object({ id, location_id: id, status: z.string(), delivery_choice: z.string(), scheduled: z.boolean(), due_at: z.string(), items: z.array(z.object({ dish_id: id, name: z.string(), quantity: z.number().int().positive(), note: z.string() })) })).max(500),
})
export type Board = z.infer<typeof boardSchema>
export type Plan = z.infer<typeof plan>
export type Stock = z.infer<typeof stock>
export type Order = Board['orders'][number]
export type Dish = Board['dishes'][number]
export type View = 'today' | 'week' | 'stock'
export const stockOptions = [{ value: 'unknown', label: 'Needs a check' }, { value: 'enough', label: 'Enough' }, { value: 'low', label: 'Running low' }, { value: 'out', label: 'Out' }] as const
export const dayKey = (date = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Lagos', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
export const daysFrom = (day: string) => Array.from({ length: 7 }, (_, index) => { const date = new Date(`${day}T12:00:00+01:00`); date.setUTCDate(date.getUTCDate() + index); return dayKey(date) })
export const dayLabel = (day: string) => new Date(`${day}T12:00:00+01:00`).toLocaleDateString('en-GB', { timeZone: 'Africa/Lagos', weekday: 'short', day: 'numeric', month: 'short' })
export const timeLabel = (date: string) => new Date(date).toLocaleTimeString('en-GB', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit' })
export const finished = (order: Order) => ['completed', 'delivered'].includes(order.status)
export const locationMatches = (order: Order, location: string) => location === 'all' || (location === 'unassigned' ? !order.location_id : order.location_id === location)
export const dailyOrders = (board: Board, location: string, day: string) => board.orders.filter((o) => locationMatches(o, location) && (dayKey(new Date(o.due_at)) === day || (day === board.start_day && !finished(o) && dayKey(new Date(o.due_at)) < day)))
export function demand(board: Board, location: string, day: string, dish: string) {
 const orders = dailyOrders(board, location, day)
 let total = 0, prepared = 0
 for (const order of orders) for (const item of order.items) if (item.dish_id === dish) {
  total += item.quantity
  if (['ready', 'completed', 'delivered', 'delivery_requested', 'in_delivery'].includes(order.status)) prepared += item.quantity
 }
 return { total, prepared, remaining: total - prepared }
}
export function orderStage(order: Order) {
 if (['paid', 'accepted'].includes(order.status)) return 'todo'
 if (order.status === 'preparing') return 'preparing'
 return 'ready'
}
export function nextStep(order: Order) {
 if (order.status === 'paid') return { status: 'accepted', label: 'Accept order' }
 if (order.status === 'accepted') return { status: 'preparing', label: 'Start prep' }
 if (order.status === 'preparing') return { status: 'ready', label: 'Mark ready' }
 if (order.status === 'ready' && order.delivery_choice === 'self_pickup') return { status: 'completed', label: 'Confirm collection' }
 return null
}
export const concreteLocation = (board: Board, location: string) => board.locations.some((l) => l.id === location)
export function savedValue(result: Record<string, unknown>, kind: 'plan' | 'stock') {
 return kind === 'plan' ? plan.parse(result.plan) : stock.parse(result.stock)
}
